import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../../services/config.service';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { RoutingService } from '../../../services/routing.service';
import { GlobalRecordService } from '../../../services/global-record.service';
import { FilingService } from '../../services/filing.service';
import { MapService } from '../../../map/services/map.service';
import { SearchService } from '../../../search/services/search.service';
import { DocViewerFactoryService } from '../../../services/factory/doc-viewer-factory.service';
import { MetadataQuickViewComponent } from '../../../views/metadata-quick-view/metadata-quick-view.component';
import { RecordQuickViewComponent } from '../../../records-management/components/record-quick-view/record-quick-view.component';
import { AuditComponent } from '../../../records-management/components/audit/audit.component';
import { TagsComponent } from '../../../components/tags/tags.component';
import { CopyLinkComponent } from '../../../components/copy-link/copy-link.component';
import { formatString } from '../../../utils/string-utils';

/**
 * Records management search results view.
 */
@Component({
	selector: 'rm-record-view',
	templateUrl: './record-view.component.html',
	styleUrl: './record-view.component.css'
})
export class RecordViewComponent implements OnInit, OnDestroy {

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;

	private initTime: number = 0;
	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	viewType?: string; // Corresponds to $stateParams.v

	records: any[] = [];

	userFields: { [key: string]: any } = {};
	validFields: any[] = [];
	searchableResultFields: any[] = [];

	visited: { [key: string]: any } = {};
	tagsEnabled: boolean = false;
	rmEnabled: boolean = false;
	mapViewEnabled: boolean = false;
	additionalIcons: boolean = false;

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private filingService: FilingService,
		public globalRecordService: GlobalRecordService,
		private routingService: RoutingService,
		private searchService: SearchService,
		private mapService: MapService,
		private docViewerFactory: DocViewerFactoryService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.initTime = Date.now();
		
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'loggedin') {
				this.processSearchResults();
			}
			else if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEnded') {
				const receivedTime = Date.now();
//				if (receivedTime - this.initTime >= 1000) {
				    this.processSearchResults();
//				}
			}
			else if (event.type === 'clearSearch') {
				this.records = [];
			}
		});

		// Listen for route changes, specifically related to the view type

		this.routingEventSubscription = this.routingService.routingEvent.subscribe(event => {
			if (event.eventType === 'navigationEnded') {
				this.processSearchResults();
			}
		});

		this.searchableResultFields = this.configService.getConfig().search.searchableResultFields;
		this.rmEnabled = this.configService.getConfig()?.recordsManagement?.enabled;
		this.mapViewEnabled = this.configService.getConfig().esri.enabled;
		this.tagsEnabled = this.configService.getConfig()?.menu?.tags;

		this.visited = this.userService.getLocalStorageData().visited;
		let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name == "DISPLAY_ADDITIONAL_ICONS"; });
		this.additionalIcons = property?.value === "true" ? true : false;
		this.processSearchResults();
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}

	// TODO
	//	 this.$on("scrollToRecord", function (evt, data) {
	//	     $location.hash("list_" + data.uuid);
	//	     $anchorScroll.yOffset = 60;
	//	     $anchorScroll();
	//	 });	

	private processSearchResults(): void {
		this.viewType = this.routingService.getQueryParam('v');
		if (!this.viewType || this.viewType !== 'R') {
			return;
		}

		console.log('Processing search results!');

		let results = this.searchService.results;

		if (!results || results.error || !this.rmEnabled) {
			this.records = [];
			return;
		}

		if (this.rmEnabled && this.validFields.length == 0) {
			if (!this.userService.user.properties) return;
			this.getValidFields();
		}

		this.records = this.searchService.processSearchResults(results, this.validFields);
	}

	private getValidFields(): void {
		this.validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => {
			return x.isAvailable;
		});

		this.userFields = {};
		this.validFields.forEach((f: any) => {
			this.userFields[f.name] = f;
		});
	}

	isValidFieldCollectionValue(field: string, value: string): boolean {
		return this.searchService.isValidCollectionValue(field, value);
	}

	/**
	 * Downloads the product.
	 */
	downloadDocument(id: number): void {
		this.alertsService.info({
			title: this.LOCALIZATION.downloadStarted,
			message: this.LOCALIZATION.processingWaitMessage,
			delay: 10000
		});

		let link = document.createElement('a');
		link.style.display = 'none';
		let url = formatString(this.configService.getConfig().document.documentDownloadURL, id);
		link.href = url;
		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
		}, 100);
	}

	/**
	 * Downloads the product metadata.
	 */
	downloadMetadata(id: number): void {
		this.alertsService.info({
			title: this.LOCALIZATION.downloadStarted,
			message: this.LOCALIZATION.processingWaitMessage,
			delay: 10000
		});

		let link = document.createElement('a');
		link.style.display = 'none';
		let url = formatString(this.configService.getConfig().document.documentDownloadURL, id) + '?metadata=true';
		link.href = url;
		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
		}, 100);
	}

	/**
	 * Displays the product in the document viewer.
	 */
	openRecord(id: number): void {
		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});
		
		modalRef.componentInstance.productId = id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);

		let userData = this.userService.getLocalStorageData();
		this.visited = userData.visited;
	}

	/**
	 * Displays the full metadata for the product.
	 */
	viewMetadata(prodid: number): void {
		const modalRef = this.ngbModal.open(MetadataQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the record management metadata for the product.
	 */
	viewRMQuickInfo(prodid: number): void {
		const modalRef = this.ngbModal.open(RecordQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the record management audit logs for the product.
	 */
	viewRecordAudit(rm_record_id: number, idx: number): void {
		const modalRef = this.ngbModal.open(AuditComponent);
		modalRef.componentInstance.record = {
			id: rm_record_id,
			title: this.records[idx].title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the tag manager for the product.
	 */
	viewTags(prodid: number, idx: number): void {
		const modalRef = this.ngbModal.open(TagsComponent);
		modalRef.componentInstance.product = {
			id: prodid,
			title: this.records[idx].title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	openInMap(record: any): void {
		this.mapService.openInMap(record, false);
	}

	/**
	 * Executes a search using the field and value.
	 */
	resultFieldSearch(field: string, value: string): void {
		console.log(`Searching for: ${field}, with value: ${value}`);

		let payload = {
			field: field,
			value: value
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	toggleRecordId(id: number): void {
		let currentValue = this.globalRecordService.getRecord(id);
		this.globalRecordService.setRecord(id, !currentValue, true);
	}

	toggleRecordComplete(id: number): void {
		this.filingService.updateRecordCompleteStatus([id], undefined, true).then(
			(response: any) => {
				// ok
			},
			(err: any) => {
				console.error("Failed to update record completion", err);
				this.alertsService.error({
					message: "Failed to update record complete status."
				});
			}
		);
	}
}
